/**
 * renderInternalRoutes builds the entire internal/staff route tree: hub, discovery,
 * admin member views, backoffice, and shared reports. It returns fragments of
 * `<Route>` elements so AppContent can embed them directly inside `<Routes>`.
 */
import { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import {
  CORPORATE_ROUTE_PERMISSION,
  getNavigationItemPermission,
  STAFF_SHELL_PERMISSION,
} from '@/config/navigation-permission-registry';
import PermissionBasedRoute, { BackofficeRoute } from '../../components/auth/PermissionBasedRoute';
import RouteListPermissionGuard from '../../components/auth/RouteListPermissionGuard';
import WriteRouteGuard from '../../components/auth/WriteRouteGuard';
import FallbackSpinner from '../../components/FallbackSpinner';
import RoleBasedRoute from '../../components/RoleBasedRoute';
import { SHOW_WORKING_AREA_BOUNDARY } from '../../design-system/constants/spacing';
import { EARLY_HUB_ALLOWED_ROLES } from '../../pages/pmx-early-ops/earlyHubConstants';
import { Client } from '../../types/clientTypes';
import { MemberRouteWrapper } from '../components/MemberRouteWrapper';
import { InClinicRedirect } from './InClinicRedirect';
import {
  Hub,
  EarlyHubRosterPage,
  EarlyHubJourneyPage,
  Appointments,
  CalendarView,
  ThriveDashboard,
  ThriveUserDashboard,
  ConsultationDashboard,
  AppDashboard,
  AppUserDetail,
  ClientDiscovery,
  ClientIVTherapy,
  ClientHBOT,
  ClientPET,
  ClientRedLightTherapy,
  ClientFIR,
  ClientPsychotherapy,
  ClientDetailsPage,
  ClientPrepPage,
  MemberPresentationPage,
  RoadmapLongFormView,
  CompareFullPage,
  ReportDataEditorPage,
  UserProfilePage,
  ClientJourneyPage,
  Studio,
  Builder,
  DesignPlayground,
  MemberHome,
  MemberProfile,
  MemberThriveOnboard,
  MemberThriveCheck,
  MemberCognitiveAssessment,
  MemberIntakeCall,
  MemberIntakeForm,
  MemberInCenterVisit,
  MemberLongevityConsultation,
  MemberSupport,
  MemberTodo,
  MemberProduct,
  MemberMarketplace,
  MemberSettings,
  MemberHealthData,
  MemberHealthSnapshot,
  MemberReports,
  MemberClinicalNotes,
  MemberPathwayDetails,
  MemberBiomarkerDetails,
  MemberThriveKommune,
  MemberNotifications,
  NotificationCenter,
  MemberCheckout,
  MemberUserVisits,
  MemberLabTest,
  MemberBoxBreathe,
  MemberFamily,
  MemberVideos,
  GiftThriveEarly,
  Backoffice,
  CreateTask,
  CreateChecklist,
  EditTask,
  EditChecklist,
  CreateProgram,
  CreateAddon,
  CreateMembership,
  CreateTeam,
  CitationDetailPage,
  UserManagement,
  IssueReports,
  IdeasPage,
  NewUserProfilePage,
  CreateUserPage,
  PodDetails,
  PodDashboard,
  TagDashboard,
  GroupDashboard,
  Dashboard,
  CorporateDashboard,
  CorporateEmployees,
  CorporateManagePage,
  FamilyDashboard,
  // Nutrition Pages
  NutritionDashboard,
  NutritionPlannerHome,
  FoodCatalogPage,
  RecipesPage,
  RecipeRecommendationsPage,
  RecipeBuilderPage,
  GuidancePage,
  MealPlansPage,
  MealPlanBuilderPage,
  TagsPage,
  PreferencesPage,
  ConditionRulesPage,
  ConditionGuidelinesPage,
  RecipeBanksPage,
  RulesPage,
  RuleSetsPage,
  IntakeLoggingPage,
  OutcomesPage,
  TemplatesPage,
  TemplateBuilderPage,
  NutritionPlanBuilderPage,
  // What's New Pages
  WhatsNewPage,
  AddReleasePage,
  EditReleasePage,
  // Knowledge Base Pages
  KnowledgeBasePage,
  AddArticlePage,
  EditArticlePage,
  ArticleViewPage,
  SOPPage,
  // Marketplace
  MarketplaceManagement,
  ServiceQueuePage,
  ClientTicketsPage,
  ClientTicketDetailPage,
  MemberMyPurchases,
  MemberMyCarts,
  MemberMyBenefits,
  // Daily Intake Log Pages
  DailyCheckinPage,
  DailyCheckinHistoryPage,
  DailyCheckinDashboardPage,
  DailyCheckinSettingsPage,
  PatientDailyLogsPage,
  // Reference Data Pages
  BiomarkerCodeMappingPage,
  PromptActionsPage,
  // Prompt Management Pages
  PromptEditorPage,
  PromptPreviewPage,
  // Wearables Pages
  PatientWearableDashboard,
  PatientWearableSleepPage,
  PatientWearableActivityPage,
  PatientWearableDailyPage,
  AdminWearablesPage,
  WearableConnectionsPage,
  ConnectDevicePage,
  OAuthCallbackPage,
  SleepListPage,
  SleepDetailPage,
  ActivityListPage,
  ActivityDetailPage,
  DailyListPage,
  DailyDetailPage,
  BodyListPage,
  ChatFullPage,
  DoctorAssignmentBackoffice,
} from './lazyPages';

type InternalRoutesProps = {
  selectedClient: Client | null;
  onMobileSidebarToggle: () => void;
  // Saved homepage for the user (already validated/expanded). When set,
  // the `/` redirect targets this instead of the default `/hub`.
  effectiveHomePath?: string | null;
  // Avoid redirecting before UI prefs have loaded so we don't flash `/hub`
  // when the user actually has a custom homepage.
  isLoadingUiPrefs?: boolean;
  showClientTickets: boolean;
};

export const renderCorporateAdminRoutes = () => (
  <Route element={<PermissionBasedRoute requiredPermission={CORPORATE_ROUTE_PERMISSION} />}>
    <Route path="/corporate" element={<CorporateDashboard />} />
    <Route path="/corporate/employees" element={<CorporateEmployees />} />
    <Route path="/corporate/employees/:userId/profile" element={<NewUserProfilePage />} />
  </Route>
);

export const renderInternalRoutes = ({
  selectedClient,
  onMobileSidebarToggle,
  effectiveHomePath,
  isLoadingUiPrefs,
  showClientTickets,
}: InternalRoutesProps) => (
  <>
    <Route element={<PermissionBasedRoute requiredPermission={STAFF_SHELL_PERMISSION} />}>
      <Route element={<RouteListPermissionGuard />}>
        <Route
          path="/"
          element={
            isLoadingUiPrefs ? (
              <FallbackSpinner />
            ) : (
              <Navigate to={effectiveHomePath ?? '/hub'} replace />
            )
          }
        />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route
          path="/whats-new/add"
          element={
            <WriteRouteGuard requiredPermission={getNavigationItemPermission('knowledge-create')}>
              <AddReleasePage />
            </WriteRouteGuard>
          }
        />
        <Route
          path="/whats-new/edit/:uuid"
          element={
            <WriteRouteGuard requiredPermission={getNavigationItemPermission('knowledge-update')}>
              <EditReleasePage />
            </WriteRouteGuard>
          }
        />
        <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
        <Route
          path="/knowledge-base/add"
          element={
            <WriteRouteGuard requiredPermission={getNavigationItemPermission('knowledge-create')}>
              <AddArticlePage />
            </WriteRouteGuard>
          }
        />
        <Route
          path="/knowledge-base/edit/:id"
          element={
            <WriteRouteGuard requiredPermission={getNavigationItemPermission('knowledge-update')}>
              <EditArticlePage />
            </WriteRouteGuard>
          }
        />
        <Route path="/knowledge-base/view/:id" element={<ArticleViewPage />} />
        <Route path="/knowledge-base/sop" element={<SOPPage />} />
        <Route path="/hub" element={<Hub />} />
        <Route element={<RoleBasedRoute allowedRoles={[...EARLY_HUB_ALLOWED_ROLES]} />}>
          <Route path="/hub/early" element={<EarlyHubRosterPage />} />
          <Route path="/hub/early/:enrollmentId" element={<EarlyHubJourneyPage />} />
        </Route>
        <Route path="/business-dashboard" element={<ThriveDashboard />} />
        <Route path="/business-dashboard/:id" element={<ThriveUserDashboard />} />
        <Route path="/app-dashboard" element={<AppDashboard />} />
        <Route path="/app-dashboard/:profileId" element={<AppUserDetail />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/chat" element={<ChatFullPage />} />
        {showClientTickets && (
          <>
            <Route path="/client-tickets" element={<ClientTicketsPage />} />
            <Route path="/client-tickets/:ticketId" element={<ClientTicketDetailPage />} />
          </>
        )}
        <Route path="/calendar" element={<CalendarView />} />
        {/* Nutrition Routes */}
        <Route path="/nutrition" element={<NutritionDashboard />} />
        <Route path="/nutrition/planner" element={<NutritionPlannerHome />} />
        <Route path="/nutrition/foods" element={<FoodCatalogPage />} />
        <Route path="/nutrition/recipes" element={<RecipesPage />} />
        <Route path="/nutrition/recipes/recommendations" element={<RecipeRecommendationsPage />} />
        <Route path="/nutrition/recipes/new" element={<RecipeBuilderPage />} />
        <Route path="/nutrition/recipes/:id" element={<RecipeBuilderPage />} />
        <Route path="/nutrition/guidance" element={<GuidancePage />} />
        <Route path="/nutrition/meal-plans" element={<MealPlansPage />} />
        <Route path="/nutrition/meal-plans/new" element={<MealPlanBuilderPage />} />
        <Route path="/nutrition/meal-plans/:id" element={<MealPlanBuilderPage />} />
        <Route path="/nutrition/tags" element={<TagsPage />} />
        <Route path="/nutrition/preferences" element={<PreferencesPage />} />
        <Route path="/nutrition/condition-rules" element={<ConditionRulesPage />} />
        <Route path="/nutrition/guidelines" element={<ConditionGuidelinesPage />} />
        <Route path="/nutrition/recipe-banks" element={<RecipeBanksPage />} />
        <Route path="/nutrition/rules" element={<RulesPage />} />
        <Route path="/nutrition/rule-sets" element={<RuleSetsPage />} />
        <Route path="/nutrition/intake" element={<IntakeLoggingPage />} />
        {/* Daily Intake Log */}
        <Route path="/nutrition/daily-checkin" element={<DailyCheckinPage />} />
        <Route path="/nutrition/daily-checkin-history" element={<DailyCheckinHistoryPage />} />
        <Route path="/nutrition/daily-checkin-dashboard" element={<DailyCheckinDashboardPage />} />
        <Route path="/nutrition/daily-checkin-settings" element={<DailyCheckinSettingsPage />} />
        <Route path="/nutrition/outcomes" element={<OutcomesPage />} />
        <Route path="/nutrition/templates" element={<TemplatesPage />} />
        <Route path="/nutrition/templates/new" element={<TemplateBuilderPage />} />
        <Route path="/nutrition/templates/:id" element={<TemplateBuilderPage />} />
        <Route path="/nutrition/plan-builder" element={<NutritionPlanBuilderPage />} />
        <Route path="/discovery" element={<ClientDiscovery selectedClient={selectedClient} />} />
        <Route
          path="/client/discovery/:clientId"
          element={<ClientDiscovery selectedClient={selectedClient} />}
        />
        <Route
          path="/client/discovery/:clientId/visit/:visitId"
          element={<ClientDiscovery selectedClient={selectedClient} />}
        />
        <Route
          path="/client/iv/:clientId/visit/:visitId"
          element={<ClientIVTherapy selectedClient={selectedClient} />}
        />
        <Route
          path="/client/hbot/:clientId/visit/:visitId"
          element={<ClientHBOT selectedClient={selectedClient} />}
        />
        <Route
          path="/client/pet/:clientId/visit/:visitId"
          element={<ClientPET selectedClient={selectedClient} />}
        />
        <Route
          path="/client/red_light/:clientId/visit/:visitId"
          element={<ClientRedLightTherapy selectedClient={selectedClient} />}
        />
        <Route
          path="/client/fir/:clientId/visit/:visitId"
          element={<ClientFIR selectedClient={selectedClient} />}
        />
        <Route
          path="/client/psychotherapy/:clientId/visit/:visitId"
          element={<ClientPsychotherapy selectedClient={selectedClient} />}
        />
        <Route path="/client/:clientId/details" element={<ClientDetailsPage />} />
        <Route path="/client/:clientId/prep" element={<ClientPrepPage />} />
        <Route path="/client/:clientId/prep/runs/:runId" element={<ClientPrepPage />} />
        <Route path="/client/:clientId/presentation" element={<MemberPresentationPage />} />
        <Route path="/client/:clientId/report-data-editor" element={<ReportDataEditorPage />} />
        <Route
          path="/client-details/:clientId/roadmap/long-form/:reportId"
          element={<RoadmapLongFormView />}
        />
        <Route
          path="/client-details/:clientId/roadmap/compare/:reportId"
          element={<CompareFullPage />}
        />
        <Route path="/client/:clientId/profile" element={<UserProfilePage />} />
        {/* Journey route: declared once here; parent allowedRoles includes ADMIN (line 191). */}
        <Route path="/client/:clientId/journey" element={<ClientJourneyPage />} />
        {/* Patient Daily Check-in Routes (staff context) */}
        <Route path="/client/:clientId/daily-checkins" element={<PatientDailyLogsPage />} />
        {/* Doctor Wearable Routes (patient context) */}
        <Route path="/client/:clientId/wearables" element={<PatientWearableDashboard />} />
        <Route path="/client/:clientId/wearables/sleep" element={<PatientWearableSleepPage />} />
        <Route
          path="/client/:clientId/wearables/activity"
          element={<PatientWearableActivityPage />}
        />
        <Route path="/client/:clientId/wearables/daily" element={<PatientWearableDailyPage />} />
        <Route path="/client/:clientId/wearables/body" element={<BodyListPage />} />
        <Route path="/client/:clientId/wearables/connect" element={<ConnectDevicePage />} />
        <Route
          path="/client/:clientId/wearables/callback/:provider"
          element={<OAuthCallbackPage />}
        />
        <Route
          path="/studio"
          element={
            <Suspense fallback={<FallbackSpinner />}>
              <Studio />
            </Suspense>
          }
        />
        <Route
          path="/reference-data/biomarker-code-mappings"
          element={
            <Suspense fallback={<FallbackSpinner />}>
              <BiomarkerCodeMappingPage />
            </Suspense>
          }
        />
        <Route
          path="/reference-data/prompt-actions"
          element={
            <Suspense fallback={<FallbackSpinner />}>
              <PromptActionsPage />
            </Suspense>
          }
        />
        <Route
          path="/builder"
          element={
            <Suspense fallback={<FallbackSpinner />}>
              <Builder />
            </Suspense>
          }
        />
        {SHOW_WORKING_AREA_BOUNDARY && (
          <Route path="/design-playground" element={<DesignPlayground />} />
        )}
        <Route element={<MemberRouteWrapper />}>
          <Route
            path="/member/home/:clientId"
            element={
              <MemberHome selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/profile/:clientId"
            element={
              <MemberProfile selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/thrive-onboard/:clientId"
            element={<MemberThriveOnboard selectedClient={null} />}
          />
          <Route
            path="/member/thrive-check/:clientId"
            element={<MemberThriveCheck selectedClient={null} />}
          />
          <Route
            path="/member/cognitive-assessment/:clientId"
            element={<MemberCognitiveAssessment selectedClient={null} />}
          />
          <Route
            path="/member/intake-call/:clientId"
            element={<MemberIntakeCall selectedClient={null} />}
          />
          <Route
            path="/member/intake-form/:clientId"
            element={<MemberIntakeForm selectedClient={null} />}
          />
          {/* Legacy in-clinic booking -> synced In Centre Visit */}
          <Route path="/member/in-clinic/:clientId" element={<InClinicRedirect />} />
          <Route
            path="/member/in-center-visit/:clientId"
            element={<MemberInCenterVisit selectedClient={null} />}
          />
          <Route
            path="/member/longevity-consultation/:clientId"
            element={<MemberLongevityConsultation selectedClient={null} />}
          />
          <Route
            path="/member/support/:clientId"
            element={
              <MemberSupport selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/todo/:clientId"
            element={
              <MemberTodo selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/product/:clientId"
            element={<MemberProduct selectedClient={null} />}
          />
          <Route
            path="/member/marketplace/:clientId"
            element={
              <MemberMarketplace
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/my-purchases/:clientId"
            element={
              <MemberMyPurchases
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/my-carts/:clientId"
            element={
              <MemberMyCarts selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/benefits/:clientId"
            element={
              <MemberMyBenefits
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/settings/:clientId"
            element={
              <MemberSettings selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/user-visits/:clientId"
            element={<MemberUserVisits selectedClient={null} />}
          />
          <Route
            path="/member/my-data/:clientId"
            element={
              <MemberHealthData
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/health-snapshot/:clientId"
            element={
              <MemberHealthSnapshot
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/reports/:clientId"
            element={
              <MemberReports selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route
            path="/member/clinical-notes/:clientId"
            element={
              <MemberClinicalNotes
                key="internal-route-clinical-notes"
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route
            path="/member/family/:clientId"
            element={
              <MemberFamily selectedClient={null} onMobileSidebarToggle={onMobileSidebarToggle} />
            }
          />
          <Route path="/member/videos/:clientId" element={<MemberVideos selectedClient={null} />} />
          <Route
            path="/member/pathway-details/:clientId/:pathwayName"
            element={<MemberPathwayDetails selectedClient={null} />}
          />
          <Route
            path="/member/biomarker-details/:clientId/:biomarkerName"
            element={<MemberBiomarkerDetails selectedClient={null} />}
          />
          <Route
            path="/member/thrive-kommune/:clientId"
            element={
              <MemberThriveKommune
                selectedClient={null}
                onMobileSidebarToggle={onMobileSidebarToggle}
              />
            }
          />
          <Route path="/notifications" element={<NotificationCenter />} />
          <Route
            path="/member/notifications/:clientId"
            element={<MemberNotifications selectedClient={null} />}
          />
          <Route path="/member/chat" element={<Navigate to="/chat" replace />} />
          <Route path="/member/checkout" element={<MemberCheckout />} />
          <Route
            path="/member/lab-test/:clientId"
            element={<MemberLabTest selectedClient={null} />}
          />
          <Route
            path="/member/box-breathe/:clientId"
            element={<MemberBoxBreathe selectedClient={null} />}
          />
          <Route
            path="/member/gift-thrive-early/:clientId"
            element={<GiftThriveEarly selectedClient={null} />}
          />
          <Route path="/member/whats-new/:clientId" element={<WhatsNewPage />} />
          <Route path="/member/knowledge-base/:clientId" element={<KnowledgeBasePage />} />
          {/* Wearables Routes (member preview) */}
          <Route path="/member/wearables/callback/:provider" element={<OAuthCallbackPage />} />
          <Route path="/member/wearables/:clientId" element={<WearableConnectionsPage />} />
          <Route path="/member/wearables/:clientId/connect" element={<ConnectDevicePage />} />
          <Route path="/member/wearables/:clientId/sleep" element={<SleepListPage />} />
          <Route path="/member/wearables/:clientId/sleep/:id" element={<SleepDetailPage />} />
          <Route path="/member/wearables/:clientId/activity" element={<ActivityListPage />} />
          <Route path="/member/wearables/:clientId/activity/:id" element={<ActivityDetailPage />} />
          <Route path="/member/wearables/:clientId/daily" element={<DailyListPage />} />
          <Route path="/member/wearables/:clientId/daily/:id" element={<DailyDetailPage />} />
          <Route path="/member/wearables/:clientId/body" element={<BodyListPage />} />
          {/* Daily Check-in Routes (member preview) */}
          <Route path="/member/daily-checkin/:clientId" element={<DailyCheckinPage />} />
          <Route
            path="/member/daily-checkin-history/:clientId"
            element={<DailyCheckinHistoryPage />}
          />
          <Route
            path="/member/daily-checkin-dashboard/:clientId"
            element={<DailyCheckinDashboardPage />}
          />
          <Route
            path="/member/daily-checkin-settings/:clientId"
            element={<DailyCheckinSettingsPage />}
          />
        </Route>
      </Route>
    </Route>

    <Route
      element={
        <PermissionBasedRoute requiredPermission={getNavigationItemPermission('consultation')} />
      }
    >
      <Route path="/bookings-dashboard" element={<ConsultationDashboard />} />
      {/* Old name (renamed to Bookings Dashboard, Oct 2026); kept so saved links still work. */}
      <Route path="/consultation-dashboard" element={<ConsultationDashboard />} />
    </Route>

    <Route element={<BackofficeRoute />}>
      <Route path="/backoffice" element={<Backoffice />} />
      <Route
        path="/backoffice/create-task"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('create-workflow-task')}>
            <CreateTask />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/create-checklist"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('create-workflow-checklist')}
          >
            <CreateChecklist />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/edit-task/:id"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('workflow-tasks-update')}
          >
            <EditTask />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/edit-checklist/:id"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('workflow-checklists-update')}
          >
            <EditChecklist />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/create-program"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('backoffice-program-create')}
          >
            <CreateProgram />
          </WriteRouteGuard>
        }
      />
      <Route path="/backoffice/doctor-assignment" element={<DoctorAssignmentBackoffice />} />
      <Route
        path="/backoffice/create-addon"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('backoffice-addon-create')}
          >
            <CreateAddon />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/create-membership"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('backoffice-membership-create')}
          >
            <CreateMembership />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/backoffice/create-team"
        element={
          <WriteRouteGuard
            requiredPermission={getNavigationItemPermission('workflow-teams-create')}
          >
            <CreateTeam />
          </WriteRouteGuard>
        }
      />
      <Route path="/backoffice/citations/new" element={<CitationDetailPage />} />
      <Route path="/backoffice/citations/:id" element={<CitationDetailPage />} />
    </Route>

    <Route
      element={<PermissionBasedRoute requiredPermission={getNavigationItemPermission('people')} />}
    >
      <Route path="/users" element={<UserManagement />} />
      <Route
        path="/users/create"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('people-create')}>
            <NewUserProfilePage />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/users/edit/:username"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('people-update')}>
            <CreateUserPage />
          </WriteRouteGuard>
        }
      />
      <Route path="/users/:username" element={<UserProfilePage />} />
      <Route
        path="/profiles/create"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('people-create')}>
            <NewUserProfilePage />
          </WriteRouteGuard>
        }
      />
      <Route path="/profiles/:userId" element={<NewUserProfilePage />} />
      <Route
        path="/profiles/:userId/edit"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('people-update')}>
            <NewUserProfilePage />
          </WriteRouteGuard>
        }
      />
      <Route path="/corporates" element={<Navigate to="/users?view=corporates" replace />} />
      <Route path="/corporates/:corporateId" element={<CorporateDashboard />} />
      <Route path="/corporates/:corporateId/manage" element={<CorporateManagePage />} />
      <Route path="/corporates/:corporateId/employees" element={<CorporateEmployees />} />
      <Route path="/families" element={<Navigate to="/users?view=families" replace />} />
      <Route path="/families/:familyId" element={<FamilyDashboard />} />
    </Route>

    <Route
      element={
        <PermissionBasedRoute requiredPermission={getNavigationItemPermission('feedback')} />
      }
    >
      <Route path="/feedback" element={<IssueReports />} />
      <Route path="/issue-reports" element={<Navigate to="/feedback?tab=ideas" replace />} />
    </Route>

    <Route
      element={<PermissionBasedRoute requiredPermission={getNavigationItemPermission('ideas')} />}
    >
      <Route path="/ideas" element={<IdeasPage />} />
    </Route>

    <Route
      element={
        <PermissionBasedRoute requiredPermission={getNavigationItemPermission('analytics')} />
      }
    >
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/pods" element={<PodDashboard />} />
      <Route path="/pods/:podId" element={<PodDetails />} />
      <Route path="/tags" element={<TagDashboard />} />
      <Route path="/groups" element={<GroupDashboard />} />
    </Route>

    <Route
      element={
        <PermissionBasedRoute requiredPermission={getNavigationItemPermission('wearables-admin')} />
      }
    >
      <Route path="/admin/wearables" element={<AdminWearablesPage />} />
    </Route>

    <Route
      element={
        <PermissionBasedRoute
          requiredPermission={getNavigationItemPermission('prompt-management')}
        />
      }
    >
      <Route
        path="/prompts/new"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('prompt-create')}>
            <PromptEditorPage />
          </WriteRouteGuard>
        }
      />
      <Route
        path="/prompts/:id/edit"
        element={
          <WriteRouteGuard requiredPermission={getNavigationItemPermission('prompt-update')}>
            <PromptEditorPage />
          </WriteRouteGuard>
        }
      />
      <Route path="/prompts/:id/preview" element={<PromptPreviewPage />} />
    </Route>

    <Route
      element={
        <PermissionBasedRoute
          requiredPermission={getNavigationItemPermission('marketplace-management')}
        />
      }
    >
      <Route
        path="/marketplace-management"
        element={
          <Suspense fallback={<FallbackSpinner />}>
            <MarketplaceManagement />
          </Suspense>
        }
      />
    </Route>

    <Route
      element={
        <PermissionBasedRoute requiredPermission={getNavigationItemPermission('service-queue')} />
      }
    >
      <Route
        path="/service-queue"
        element={
          <Suspense fallback={<FallbackSpinner />}>
            <ServiceQueuePage />
          </Suspense>
        }
      />
    </Route>

    {renderCorporateAdminRoutes()}
  </>
);
